/** EPBCS approval workflow — Action button + dialog, reusable on every budgeting form. */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lightning, X, Paperclip } from "@phosphor-icons/react";
import { Field, Input, Select, Btn } from "./common";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-500", first_pass: "bg-slate-100 text-slate-600",
  awaiting_clarification: "bg-blue-50 text-blue-700",
  under_review: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  draft: "Draft", first_pass: "First Pass",
  awaiting_clarification: "Awaiting Clarification", under_review: "Under Review",
  approved: "Approved", rejected: "Rejected",
};
const ALL_ACTIONS = {
  take_ownership: "Take Ownership",
  clarify: "Assign for Clarification",
  promote: "Promote",
  reassign: "Reassign",
  approve: "Approve",
  reject: "Reject",
  reopen: "Reopen (admin)",
  comment: "Comment",
};
const ACTIONS_BY_STATUS = {
  draft: ["take_ownership", "comment"],
  first_pass: ["promote", "clarify", "reassign", "take_ownership", "comment"],
  awaiting_clarification: ["promote", "clarify", "reassign", "comment"],
  under_review: ["approve", "reject", "reassign", "comment"],
  approved: ["reopen", "comment"],
  rejected: ["promote", "clarify", "reassign", "comment"],
};
const PROMOTE_HINT = {
  first_pass: "Promote → Under Review",
  awaiting_clarification: "Promote → back to First Pass",
};

export const ActionButton = ({ section, pov, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [action, setAction] = useState("comment");
  const [comment, setComment] = useState("");
  const [tag, setTag] = useState("");
  const [assignee, setAssignee] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !pov) return;
    api.post("/epbcs/approvals/list", { pov }).then((r) => {
      const a = r.data.approvals.find((x) => x.section === section);
      setInfo(a);
      const allowed = ACTIONS_BY_STATUS[a?.status || "draft"] || ["comment"];
      setAction(allowed[0]);
    }).catch(() => {});
  }, [open, pov, section]);

  const submit = async () => {
    setBusy(true);
    try {
      const attachments = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/epbcs/approvals/upload", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        attachments.push(r.data);
      }
      const { data } = await api.post("/epbcs/approvals/action",
        { pov, section, action, comment, tag, assignee, attachments });
      toast.success(data.notified?.length
        ? `Done — notified ${data.notified.join(", ")}`
        : "Action recorded");
      setOpen(false); setComment(""); setTag(""); setAssignee(""); setFiles([]);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`wf-action-btn-${section}`}
        className="inline-flex items-center gap-1.5 border border-[#C8A24B] text-[#0B1E3F] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded hover:bg-[#C8A24B]/10 transition-colors whitespace-nowrap">
        <Lightning size={12} weight="fill" className="text-[#C8A24B]" /> Action
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1E3F]/40 p-4"
          data-testid="wf-action-dialog" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-md shadow-xl border border-slate-200"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 bg-[#0B1E3F] text-white">
              <span className="text-[12px] font-bold uppercase tracking-wider">
                Workflow Action — {section}
              </span>
              <button onClick={() => setOpen(false)} data-testid="wf-dialog-close"
                className="text-gray-300 hover:text-white"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              {info && (
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <span className={`px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider text-[10px] ${STATUS_STYLE[info.status]}`}
                    data-testid="wf-dialog-status">{STATUS_LABEL[info.status]}</span>
                  {info.owner && <span className="text-slate-500">Owner: <b>{info.owner}</b></span>}
                  {info.clarification_assignee && info.status === "awaiting_clarification" && (
                    <span className="text-blue-700">Clarification: <b>{info.clarification_assignee}</b></span>
                  )}
                  {info.assignment && (
                    <span className="text-slate-400 w-full">
                      Assigned — owner: {info.assignment.owner}
                      {info.assignment.alt_owner ? ` / ${info.assignment.alt_owner}` : ""} ·
                      reviewer: {info.assignment.reviewer}
                    </span>
                  )}
                </div>
              )}
              <Field label="Action">
                <Select value={action} data-testid="wf-action-select"
                  onChange={(e) => setAction(e.target.value)}>
                  {(ACTIONS_BY_STATUS[info?.status || "draft"] || ["comment"]).map((k) => (
                    <option key={k} value={k}>
                      {k === "promote" ? (PROMOTE_HINT[info?.status] || "Promote") : ALL_ACTIONS[k]}
                    </option>
                  ))}
                </Select>
              </Field>
              {["clarify", "reassign"].includes(action) && (
                <Field label={action === "clarify" ? "Clarification assignee (email)" : "New owner (email)"}>
                  <Input data-testid="wf-assignee-input" placeholder="user@company.com"
                    value={assignee} onChange={(e) => setAssignee(e.target.value)} />
                </Field>
              )}
              <Field label="Comment">
                <textarea data-testid="wf-comment-input" rows={3}
                  className="w-full border border-slate-300 px-2 py-1.5 text-[12px] rounded-sm focus:border-[#0B1E3F] outline-none"
                  placeholder="Add a comment…"
                  value={comment} onChange={(e) => setComment(e.target.value)} />
              </Field>
              <Field label="Tag (project / task / asset reference)">
                <Input data-testid="wf-tag-input" placeholder="e.g. CC100_27_001 · Task T1"
                  value={tag} onChange={(e) => setTag(e.target.value)} />
              </Field>
              <Field label="Attachments (max 5 MB each)">
                <label className="flex items-center gap-2 border border-dashed border-slate-300 px-3 py-2 text-[11px] text-slate-500 cursor-pointer hover:border-[#C8A24B]">
                  <Paperclip size={14} />
                  {files.length ? files.map((f) => f.name).join(", ") : "Attach supporting documents…"}
                  <input type="file" multiple className="hidden" data-testid="wf-file-input"
                    onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                </label>
              </Field>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setOpen(false)}
                  className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">
                  Cancel
                </button>
                <Btn onClick={submit} data-testid="wf-action-submit"
                  disabled={busy || (action === "comment" && !comment && files.length === 0) ||
                    (["clarify", "reassign"].includes(action) && !assignee)}>
                  {busy ? "Working…" : "Execute"}
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const sectionForForm = (form) =>
  form.startsWith("revenue") ? "revenue"
    : form.startsWith("opex") ? "opex"
    : form.startsWith("fin") ? "financials" : "financials";
